import React, { useCallback, useEffect, useMemo, useState } from 'react';

const initialFormState = {
  chain: 'solana',
  token_symbol: '',
  token_address: '',
  decimals: 9,
  primary_user_id: '',
  token_image_url: '',
  metadata_url: ''
};

export default function AdminTokens() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
  }), []);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/ledger/tokens', { headers });
      if (!res.ok) throw new Error('Failed to fetch tokens');
      const json = await res.json();
      setItems(json.data || []);
    } catch (e) {
      console.error('Failed to load tokens', e);
      setError('Failed to load tokens');
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: field === 'decimals' ? Number(value) : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        ...form,
        primary_user_id: form.primary_user_id ? Number(form.primary_user_id) : null
      };

      const response = await fetch('/api/ledger/tokens', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.message || json.error || 'Failed to add token');
      }

      setSuccess('Token added successfully');
      setForm(initialFormState);
      await load();
    } catch (e) {
      console.error('Failed to add token', e);
      setError(e?.message || 'Failed to add token');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`/api/ledger/tokens/${id}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.message || json.error || 'Failed to delete token');
      }

      setSuccess('Token removed');
      await load();
    } catch (e) {
      console.error('Failed to delete token', e);
      setError(e?.message || 'Failed to delete token');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <h2 className="text-2xl font-bold text-white">Token Registry</h2>
        {(success || error) && (
          <div
            className={`px-4 py-2 rounded-lg text-sm ${
              success ? 'bg-green-900 text-green-200 border border-green-600' : 'bg-red-900 text-red-200 border border-red-600'
            }`}
          >
            {success || error}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="bg-gray-900 bg-opacity-70 border border-gray-700 rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">Chain</label>
            <select
              value={form.chain}
              onChange={handleChange('chain')}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="solana">Solana</option>
              <option value="sui">Sui</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">Symbol</label>
            <input
              value={form.token_symbol}
              onChange={handleChange('token_symbol')}
              placeholder="Token symbol"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-semibold text-gray-300 mb-2">Address / Mint</label>
            <input
              value={form.token_address}
              onChange={handleChange('token_address')}
              placeholder="Token mint address"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">Decimals</label>
            <input
              type="number"
              value={form.decimals}
              onChange={handleChange('decimals')}
              min={0}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-semibold text-gray-300 mb-2">Image URL</label>
            <input
              value={form.token_image_url}
              onChange={handleChange('token_image_url')}
              placeholder="https://..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-semibold text-gray-300 mb-2">Metadata URL</label>
            <input
              value={form.metadata_url}
              onChange={handleChange('metadata_url')}
              placeholder="https://..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">Primary User ID (optional)</label>
            <input
              value={form.primary_user_id}
              onChange={handleChange('primary_user_id')}
              placeholder="User ID"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
          >
            {loading ? 'Saving...' : 'Add Token'}
          </button>
        </div>
      </form>

      <div className="bg-gray-900 bg-opacity-70 border border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900 bg-opacity-80">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Chain</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Symbol</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Address</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Decimals</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Primary User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Image</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {(items || []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-400 text-sm">
                    No tokens registered yet.
                  </td>
                </tr>
              ) : (
                (items || []).map((it) => (
                  <tr key={it.id} className="hover:bg-gray-800">
                    <td className="px-4 py-3 text-sm text-gray-200 uppercase">{it.chain}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-white">{it.token_symbol}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-300 break-all">{it.token_address || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{it.decimals}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{it.primary_user_id || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {it.token_image_url ? (
                        <img
                          src={it.token_image_url}
                          alt={it.token_symbol}
                          className="h-10 w-10 rounded-full object-cover border border-gray-700"
                        />
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(it.id)}
                        disabled={loading}
                        className="text-sm text-red-400 hover:text-red-200 disabled:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


